import { Component, inject, Input } from '@angular/core';
import { ButtonModule } from '@ui/form/button/button.module';
import { ButtonRowModule } from '@ui/form/button-row/button-row.module';
import { CalendarControlModule } from '@ui/form/calendar-control/calendar-control.module';
import { FormGroupWrapperModule } from '@ui/form/form-group-wrapper/form-group-wrapper.module';
import { FormInputComponent } from '@ui/form/form-input/form-input.component';
import { FormSelectComponent } from '@ui/form/form-select/form-select.component';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { ProgramFormComponent } from '../program-form.component';
import { ProgramSingleProductFormField, ProgramSingleProductFormService } from './program-single-product-form.service';
import { AbstractListModel } from '@libs/factory/abstract-list/abstract-list.model';
import { B2bProgramRate } from '../../../../shared/models/b2b-program-rate.model';
import { PATTERN_NUMERIC, PATTERN_PERCENTAGE } from '@libs/parameters/patterns';
import { PartnerProgram } from '@api/models/B2bService/Contract/partner-program';
import { B2bProgramType } from '../../../../shared/models/b2b-program-type';
import { TranslatePipe } from '@ngx-translate/core';
import { NgSelectComponent } from '@ng-select/ng-select';


@Component({
    selector: 'b2b-program-single-product-form',
    imports: [
        ButtonModule,
        ButtonRowModule,
        CalendarControlModule,
        FormGroupWrapperModule,
        FormInputComponent,
        FormSelectComponent,
        ReactiveFormsModule,
        TranslatePipe,
        NgSelectComponent,
    ],
    templateUrl: './program-single-product-form.component.html',
    styleUrl: './program-single-product-form.component.scss',
    providers: [ProgramSingleProductFormService],
})
export class ProgramSingleProductFormComponent extends ProgramFormComponent {
    /**
     * List unit types
     */
    @Input() public unit: AbstractListModel[];
    /**
     * List rate types
     */
    @Input() public type: AbstractListModel[];

    readonly #programFormService: ProgramSingleProductFormService = inject(ProgramSingleProductFormService);

    protected readonly programSingleProductFormField: typeof ProgramSingleProductFormField = ProgramSingleProductFormField;
    protected readonly b2bProgramTypeEnum: typeof B2bProgramType = B2bProgramType;
    protected readonly b2bProgramRate: typeof B2bProgramRate = B2bProgramRate;
    /**
     * Pattern percent
     */
    public readonly PATTERN_PERCENTAGE: string = PATTERN_PERCENTAGE;
    /**
     * Pattern numeric
     */
    public readonly PATTERN_NUMERIC: string = PATTERN_NUMERIC;

    public get currDiscountRateType(): AbstractControl {
        return this.form.get(ProgramSingleProductFormField.CurrDiscountRateType);
    }

    public get nextDiscountRateType(): AbstractControl {
        return this.form.get(ProgramSingleProductFormField.NextDiscountRateType);
    }

    public get currFeeRateType(): AbstractControl {
        return this.form.get(ProgramSingleProductFormField.CurrFeeRateType);
    }

    public get nextFeeRateType(): AbstractControl {
        return this.form.get(ProgramSingleProductFormField.NextFeeRateType);
    }

    public get prgTypeValue(): B2bProgramType {
        return this.form.get(ProgramSingleProductFormField.PrgType)?.value;
    }

    constructor() {
        super();
        this.form = this.#programFormService.createReactiveForm();
    }

    /**
     * B2b program data for form fill
     */
    @Input()
    public set b2bProgram(b2bProgram: PartnerProgram) {
        if (b2bProgram) {
            this.trfName = b2bProgram.trfName;
            this.prtName = b2bProgram.orgName;

            const { nextFeeDate, nextDiscountDate, prgName }: Partial<PartnerProgram> = b2bProgram;

            this.form.patchValue({
                ...b2bProgram,
                name: prgName,
                ...(nextFeeDate ? { nextFeeDate: new Date(nextFeeDate) } : null),
                ...(nextDiscountDate ? { nextDiscountDate: new Date(nextDiscountDate) } : null),
            });

            this.#programFormService.defineMechanicsAfterPatchForm();
        }
    }

    public handlerAdd(): void {
        const { currDiscountRate, currFeeRate }: Partial<PartnerProgram> = this.form.getRawValue();

        this.submitForm.emit({
            ...this.form.getRawValue(),
            currDiscountRate: Number(currDiscountRate),
            currFeeRate: Number(currFeeRate),
        });
    }

    public handlerEdit(): void {
        const {
            currDiscountRate,
            currFeeRate,
            nextFeeRate,
            nextDiscountRate,
        }: {
            currDiscountRate: string;
            currFeeRate: string;
            nextFeeRate: string;
            nextDiscountRate: string;
        } = this.form.getRawValue();

        this.submitForm.emit({
            ...this.form.getRawValue(),
            nextDiscountRate: nextDiscountRate === '' ? null : Number(nextDiscountRate),
            currDiscountRate: currDiscountRate === '' ? null : Number(currDiscountRate),
            prgId: this.prgId,
            currFeeRate: currFeeRate === '' ? null : Number(currFeeRate),
            nextFeeRate: nextFeeRate === '' ? null : Number(nextFeeRate),
        });
    }
}
